import React, { useCallback, useContext, useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import PropTypes from 'prop-types';

import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import {
  setInitialJournalBasedOnRecommendation,
  setInitialJournalScopeBasedOnAccount,
  setUpJournalForSubmit,
} from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import { getAccountOptions } from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';

export default function QuickBookRowSecurityDepositReceiveTemplate({ transaction, onTransactionAction, onGetHelp }) {
  const [isLoading, setIsLoading] = useState(true);
  const [securityDepositAccountOptions, setSecurityDepositAccountOptions] = useState([]);
  const [journal, setJournal] = useState(getManualBasicJournal());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { organizationId, organizationName, triggerSnackbar } = useContext(PersonContext);
  const tracking = useSegmentTrack();
  const classes = useStyles();
  const isMobile = checkIsSmallScreen();

  const fetchSecurityDepositAccount = useCallback(async (organizationId) => {
    setIsLoading(true);

    const securityDepositAccounts = await getAccountOptions(organizationId, {
      type2: 'Security Deposits',
      inactive: { $or: [null, false] },
    });
    setSecurityDepositAccountOptions(securityDepositAccounts);

    setIsLoading(false);
  }, []);

  async function addSecurityDeposit(event) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const journalBody = setUpJournalForSubmit({
        transactionId: transaction.id,
        journal,
        organizationId,
        type: 'Security Deposit In',
        direction: 'cash in',
        bookAsRefund: false,
      });
      await create('journals', journalBody);

      setIsSubmitting(false);
      onTransactionAction();
    } catch (error) {
      triggerSnackbar({
        horizontal: 'center',
        message: error?.message || 'Something went wrong. Please try again.',
        type: 'error',
        vertical: 'top',
      });
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    fetchSecurityDepositAccount(organizationId);
  }, [fetchSecurityDepositAccount, organizationId]);

  useEffect(() => {
    const setInitialJournalValues = async () => {
      let scopeObject = {};
      const hasV1Recommendation = !!transaction.recommendedAccountId;
      if (hasV1Recommendation) {
        setInitialJournalBasedOnRecommendation({
          transaction,
          journalObject: journal,
          accountOptions: securityDepositAccountOptions,
          targetJournalLine: journal.creditLines[0],
          organizationName,
        });
      } else {
        const scope = await setInitialJournalScopeBasedOnAccount(transaction.account, {});
        scopeObject.journalScopeSelect = scope.journalScopeSelect;
        scopeObject.propertyIdSelect = scope.propertyIdSelect || null;
        scopeObject.unitIdSelect = scope.unitIdSelect || null;
        scopeObject.entityIdSelect = scope.entityIdSelect || null;
      }
      setJournal((prevJournal) => {
        const newJournal = { ...prevJournal };
        newJournal.debitLines[0].accountIdSelect = transaction.account;
        newJournal.debitLines[0].transactionId = transaction.id;
        if (securityDepositAccountOptions.length === 1) {
          newJournal.creditLines[0].accountIdSelect = securityDepositAccountOptions[0];
        }
        newJournal.description = transaction.description;
        newJournal.date = transaction.date;
        newJournal.amount = transaction.amount;
        if (hasV1Recommendation) {
          newJournal.journalScopeSelect = journal.journalScopeSelect;
          newJournal.propertyIdSelect = journal.propertyIdSelect || null;
          newJournal.unitIdSelect = journal.unitIdSelect || null;
          newJournal.entityIdSelect = journal.entityIdSelect || null;
        } else {
          newJournal.journalScopeSelect = scopeObject.journalScopeSelect;
          newJournal.propertyIdSelect = scopeObject.propertyIdSelect;
          newJournal.unitIdSelect = scopeObject.unitIdSelect;
          newJournal.entityIdSelect = scopeObject.entityIdSelect;
        }
        return newJournal;
      });
    };
    setInitialJournalValues();
  }, [transaction, securityDepositAccountOptions, organizationName]);

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <form onSubmit={addSecurityDeposit}>
      <div className={classes.rootContainer}>
        <div className={classes.container}>
          <QuickBookRowAutocomplete
            className={classes.autoCompleteField}
            displaySubaccounts
            options={securityDepositAccountOptions}
            onChange={(value) => {
              setJournal({ ...journal, creditLines: [{ ...journal.creditLines[0], accountIdSelect: value }] });
            }}
            label="Security Deposit Account"
            value={journal.creditLines[0]?.accountIdSelect}
            required
          />
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
        </div>
        <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
        <QuickBookRowNotesAndAttachment journal={journal} onChange={setJournal} />
        <div className={classes.actionsContainer}>
          <Button
            variant="outlined"
            size="small"
            className={classes.helpButton}
            onClick={() => {
              tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
              onGetHelp();
            }}
          >
            <HelpOutline className={classes.helpIcon} />
            Get Help
          </Button>
          <div className={classes.rowActionsSecondaryContainer}>
            <Button
              className={classes.saveButton}
              size="small"
              type="submit"
              variant="contained"
              onClick={() => {
                tracking('save clicked', { location: 'import_feed rei' });
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

QuickBookRowSecurityDepositReceiveTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func.isRequired,
  onGetHelp: PropTypes.func.isRequired,
};
